import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { IconButton } from "@material-ui/core";
import {
  CheckCircle as CheckCircleIcon,
  FiberManualRecord as FiberManualRecordIcon,
  FiberManualRecordOutlined as FiberManualRecordOutlinedIcon,
  LocalShipping as LocalShippingIcon,
  MoreHoriz as MoreIcon,
} from "@material-ui/icons";
import {
  ActionList,
  Badge,
  Button,
  Card,
  ChoiceList,
  DataTable,
  Filters,
  Pagination,
  Popover,
  Select,
} from "@shopify/polaris";
import { useAdmin } from "context/admin";
import { useUser } from "context/user";
import useLocalStorage from "hooks/useLocalStorage";
import { useReactToPrint } from "react-to-print";
import { STORE_PROVIDER } from "shared/constants";
import {
  buildOrderListResults,
  isBackupRateDeliveryError,
} from "util/orderListPipeline";
import {
  getOrderDepartments,
  getOrderReadyByLabel,
} from "util/orderOrganizer";
import { orderShippingMethod, parseOrderData } from "util/helpers";
import ParcelLabelModal from "views/Modals/ParcelLabelModal";
import PrintLineItemSummary from "./PrintLineItemSummary";
import PrintOrders from "./PrintOrders";
import ScheduleDeliveryModal from "./ScheduleDeliveryModal";
import SelectTimeSlotModal from "./SelectTimeSlotModal";
import ChangeFulfillmentLocationModal from "./components/ChangeFulfillmentLocationModal";
import OrderCollectionDate from "./components/OrderCollectionDate";
import OrderServiceType from "./components/OrderServiceType";
// import useWhyDidYouUpdate from 'hooks/useWhyDidYouUpdate';

const OrderList = React.memo(
  ({ orders, deliveries, handleSelect, tab, title = "Orders", date }) => {
    // useWhyDidYouUpdate('OrderList', { orders, deliveries, handleSelect, tab, title });

    const REFUNDABLE_CREDIT_CARD_HOLD_TITLE = "Credit Card Hold";
    const pageSize = 10;

    const user = useUser();
    const admin = useAdmin();

    const pageKey = `ordersPage-${admin.selectedShopId}-${tab}`;
    const [page, setPage] = useLocalStorage(pageKey, 1);
    const resultsRef = useRef([]);
    const skipFilterPageReset = useRef(true);
    const [sortKey, setSortKey] = useState("order_number");
    const [sortDir, setSortDir] = useState("desc");
    const [lineItemSummary, setLineItemSummary] = useState([]);
    const [popover, setPopover] = useState(false);
    const [loadingLineItemSummary, setLoadingLineItemSummary] = useState(false);
    const [scheduleDeliveryFor, setScheduleDeliveryFor] = useState();
    const [changeDateFor, setChangeDateFor] = useState();
    const [schedulingDelivery, setSchedulingDelivery] = useState(false);
    const [changingOrderDate, setChangingOrderDate] = useState(false);
    const [changeLocationFor, setChangeLocationFor] = useState();
    const [changingLocation, setChangingLocation] = useState(false);
    const [location, setLocation] = useState(null);
    const [parcelLabelOpen, setParcelLabelOpen] = useState(false);
    const [parcelLabelLoading, setParcelLabelLoading] = useState(false);
    const [parcelLabelUrl, setParcelLabelUrl] = useState(null);
    const [parcelLabelFilename, setParcelLabelFilename] = useState(null);
    // enable location filter for sanagans
    const [shippingMethod, setShippingMethod] = useState(null);
    const [queryValue, setQueryValue] = useState(null);
    const [selectedDepartments, setSelectedDepartments] = useState(null);
    const [selectedPickupTimes, setSelectedPickupTimes] = useState(null);
    // Pickup instructions email is disabled for now
    const [disableEmailer, setDisableEmailer] = useState(true);
    const printOrderComponentRef = useRef();
    const shop = admin.selectedShop;
    const ordersOrganizerEnabled =
      shop?.shop_config?.orders_organizer_enabled === true;
    const sanagans =
      shop.shopify_domain.indexOf("sanagans") !== -1 ||
      shop.shopify_domain.indexOf("dev-sa") !== -1 ||
      shop.shopify_domain.indexOf("friendly") !== -1;

    // 1. Add selectedFilterDate state at the top with other states
    const [selectedFilterDate, setSelectedFilterDate] = useState(null);

    // 1. Memoize the getUniqueDatesFromOrders function and its result
    const getUniqueDatesFromOrders = useCallback((ordersToProcess) => {
      const uniqueDatesMap = new Map();

      ordersToProcess.forEach((order) => {
        try {
          const { year, month, day } = parseOrderData(order);
          if (year && month && day) {
            const collectionDate = new Date(year, month - 1, day);
            const dateString = collectionDate.toLocaleDateString();
            uniqueDatesMap.set(dateString, true);
          }
        } catch (error) {
          console.log(error);
        }
      });

      return Array.from(uniqueDatesMap.keys()).sort(
        (a, b) => new Date(a) - new Date(b),
      );
    }, []);

    // 3. Define AvailablePickupLocations before filters
    const AvailablePickupLocations = orders
      .map((order) => {
        if (
          order.fulfillment_order &&
          order.fulfillment_order.assigned_location
        ) {
          return order.fulfillment_order.assigned_location?.address1;
        } else if (
          order.fulfillments &&
          order.fulfillments[0] &&
          admin.selectedShop.locations
        ) {
          let location_id = order.fulfillments[0].location_id;
          let thisLocation = admin.selectedShop.locations.filter(
            (l) => l.id === location_id,
          );
          return thisLocation[0].address1;
        }
        return "Cancelled";
      })
      .filter((v, i, a) => v && a.indexOf(v) === i);

    const handlePrintOrders = useReactToPrint({
      content: () => printOrderComponentRef.current,
    });
    const printItemSummaryComponentRef = useRef();
    const handlePrintItemSummary = useReactToPrint({
      content: () => printItemSummaryComponentRef.current,
      onBeforeGetContent: () => {
        return new Promise((resolve) => {
          const toPrint = resultsRef.current;
          if (!toPrint) {
            resolve();
            return;
          }

          setLoadingLineItemSummary(true);
          const lineItemDict = {};

          toPrint.forEach((order) => {
            order.line_items.forEach((line_item) => {
              if (
                line_item.title.indexOf(REFUNDABLE_CREDIT_CARD_HOLD_TITLE) ===
                  -1 &&
                !line_item.title
                  .toLowerCase()
                  .includes("picking & handling fee")
              ) {
                let item = lineItemDict[line_item.variant_id];

                if (item) {
                  lineItemDict[line_item.variant_id] = {
                    ...item,
                    total: (item.total || 0) + line_item.quantity,
                    order_counts: {
                      ...item.order_counts,
                      [order.name]: item[order.name]
                        ? item[order.name] + line_item.quantity
                        : line_item.quantity,
                    },
                  };
                } else {
                  lineItemDict[line_item.variant_id] = {
                    order_counts: {
                      [order.name]: line_item.quantity,
                    },
                    variant_title: line_item.variant_title,
                    title: line_item.title,
                    total: line_item.quantity,
                  };
                }
              }
            });
          });

          const list = Object.keys(lineItemDict)
            .map((variant_id) => lineItemDict[variant_id])
            .sort((a, b) => b.total - a.total);

          setLoadingLineItemSummary(false);
          setLineItemSummary(list);
          resolve(true);
        });
      },
      suppressErrors: true,
    });

    const [locationsLoaded, setLocationsLoaded] = useState(false);

    // Extract the complex expression to a variable
    const selectedShopLocations = admin.selectedShop?.locations;

    // Memoize the locations to avoid recalculating on every render
    const locationsMap = useMemo(() => {
      if (!selectedShopLocations) return {};
      return selectedShopLocations.reduce((map, location) => {
        map[location.id] = location.address1;
        return map;
      }, {});
    }, [selectedShopLocations]);

    useEffect(() => {
      if (selectedShopLocations) {
        setLocationsLoaded(true);
      }
    }, [selectedShopLocations]);

    const getLocationName = useCallback(
      (address1) => {
        if (!selectedShopLocations) {
          return null;
        }
        if (address1 === null || address1 === undefined) {
          return "Cancelled";
        }
        const locationName = selectedShopLocations.find(
          (l) => l.address1 === address1,
        );
        return locationName?.name || "Unknown Location";
      },
      [selectedShopLocations],
    );

    const handleEmailPickupInstructions = () => {
      const emailKey = "sanagans_pickup_instructions";
      const ordersToEmail = orders.filter(
        (o) =>
          o.sent_emails.indexOf(emailKey) === -1 && o.service.type === "pickup",
      );
      if (date && ordersToEmail.length > 0) {
        admin.setConfirmation({
          content: `Are you sure you want to email pickup instructions? Emails will be sent to orders ${ordersToEmail
            .map((o) => o.name)
            .join(", ")}.`,
          handleOk: () => {
            admin.emailPickupInstructions(date.toLocaleDateString("en-ca"));
            setDisableEmailer(true);
          },
        });
      } else if (date) {
        admin.setToast(
          `There are no pickup orders that haven't already been emailed for ${date.toDateString()}.`,
        );
      }
    };

    const serviceWindows =
      (admin.selectedShop ? admin.selectedShop.service_windows : []) || [];

    const handleShippingMethodChange = useCallback((value) => {
      // Only take the last one that was selected
      if (value && value.length === 0) {
        value = null;
      }
      if (value && value.length > 1) {
        value = [value[value.length - 1]];
      }
      setShippingMethod(value);
    }, []);
    const handleFiltersQueryChange = useCallback(
      (value) => setQueryValue(value),
      [],
    );
    const handleLocationRemove = useCallback(() => setLocation(null), []);
    const handleShippingMethodRemove = useCallback(
      () => setShippingMethod(null),
      [],
    );
    const handleQueryValueRemove = useCallback(() => setQueryValue(null), []);
    const handleDepartmentsRemove = useCallback(
      () => setSelectedDepartments(null),
      [],
    );
    const handlePickupTimesRemove = useCallback(
      () => setSelectedPickupTimes(null),
      [],
    );
    const handleFiltersClearAll = useCallback(() => {
      handleShippingMethodRemove();
      handleQueryValueRemove();
      handleLocationRemove();
      setSelectedFilterDate(null);
      setSelectedDepartments(null);
      setSelectedPickupTimes(null);
    }, [
      handleShippingMethodRemove,
      handleQueryValueRemove,
      handleLocationRemove,
    ]);

    const handleLocationChange = useCallback((value) => {
      // Only take the last one that was selected
      if (value && value.length === 0) {
        value = null;
      }
      if (value && value.length > 1) {
        value = [value[value.length - 1]];
      }
      setLocation(value);
    }, []);

    const handleDateFilterRemove = useCallback(
      () => setSelectedFilterDate(null),
      [],
    );

    const uniqueDepartmentChoices = useMemo(() => {
      const s = new Set();
      orders.forEach((o) => {
        getOrderDepartments(o).forEach((d) => s.add(d));
      });
      return Array.from(s).sort((a, b) => a.localeCompare(b));
    }, [orders]);

    const uniquePickupChoices = useMemo(() => {
      const s = new Set();
      orders.forEach((o) => {
        const lab = getOrderReadyByLabel(o);
        if (lab) s.add(lab);
      });
      return Array.from(s).sort();
    }, [orders]);

    const appliedFilters = useMemo(() => {
      const list = [];
      if (shippingMethod?.length) {
        list.push({
          key: "shippingMethod",
          label: `Shipping Method: ${shippingMethod.join(",")}`,
          onRemove: handleShippingMethodRemove,
        });
      }
      if (location?.length) {
        list.push({
          key: "location",
          label: `Location: ${location}`,
          onRemove: handleLocationRemove,
        });
      }
      if (selectedFilterDate) {
        list.push({
          key: "date",
          label: `Date: ${selectedFilterDate}`,
          onRemove: handleDateFilterRemove,
        });
      }
      if (ordersOrganizerEnabled && selectedDepartments?.length) {
        list.push({
          key: "departments",
          label: `Department: ${selectedDepartments.join(", ")}`,
          onRemove: handleDepartmentsRemove,
        });
      }
      if (ordersOrganizerEnabled && selectedPickupTimes?.length) {
        list.push({
          key: "pickupTimes",
          label: `Pickup time: ${selectedPickupTimes.join(" · ")}`,
          onRemove: handlePickupTimesRemove,
        });
      }
      return list;
    }, [
      shippingMethod,
      location,
      selectedFilterDate,
      selectedDepartments,
      selectedPickupTimes,
      ordersOrganizerEnabled,
      handleShippingMethodRemove,
      handleLocationRemove,
      handleDateFilterRemove,
      handleDepartmentsRemove,
      handlePickupTimesRemove,
    ]);

    const results = useMemo(
      () =>
        buildOrderListResults(orders, {
          selectedFilterDate,
          location,
          shippingMethod,
          selectedDepartments,
          selectedPickupTimes,
          queryValue,
          sortKey,
          sortDir,
          organizerEnabled: ordersOrganizerEnabled,
          deliveries,
          getLocationName,
          shopLocations: selectedShopLocations,
        }),
      [
        orders,
        selectedFilterDate,
        location,
        shippingMethod,
        selectedDepartments,
        selectedPickupTimes,
        queryValue,
        sortKey,
        sortDir,
        ordersOrganizerEnabled,
        deliveries,
        getLocationName,
        selectedShopLocations,
      ],
    );

    resultsRef.current = results;

    const rows = useMemo(
      () =>
        results.slice((page - 1) * pageSize, page * pageSize),
      [results, page, pageSize],
    );

    useEffect(() => {
      setPage(1);
      setShippingMethod(null);
      setLocation(null);
      setQueryValue(null);
      setSelectedFilterDate(null);
      setSelectedDepartments(null);
      setSelectedPickupTimes(null);
      // setPage intentionally omitted: useLocalStorage returns an unstable setter
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    useEffect(() => {
      if (skipFilterPageReset.current) {
        skipFilterPageReset.current = false;
        return;
      }
      setPage(1);
      // setPage intentionally omitted: useLocalStorage returns an unstable setter
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      queryValue,
      shippingMethod,
      location,
      selectedFilterDate,
      selectedDepartments,
      selectedPickupTimes,
    ]);

    useEffect(() => {
      const lastPage = Math.max(1, Math.ceil(results.length / pageSize) || 1);
      if (page > lastPage) setPage(lastPage);
      // setPage intentionally omitted: useLocalStorage returns an unstable setter
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results.length, page, pageSize]);

    // Update the filters array to include the date filter for the upcoming tab
    const filters = [
      {
        key: "shippingMethod",
        label: "Shipping Method",
        filter: (
          <ChoiceList
            title="Shipping Method"
            titleHidden
            choices={[
              { label: "Pickup", value: "Pickup" },
              { label: "Courier Delivery", value: "Courier Delivery" },
              { label: "Store Delivery", value: "Store Delivery" },
            ]}
            selected={shippingMethod || []}
            onChange={handleShippingMethodChange}
            allowMultiple
          />
        ),
        shortcut: true,
      },
    ];

    if (sanagans) {
      filters.unshift({
        key: "pickupLocation",
        label: "Pickup Location",
        filter: (
          <ChoiceList
            title="Pickup Location"
            titleHidden
            choices={AvailablePickupLocations.map((location) => ({
              label:
                location === "Cancelled"
                  ? "Cancelled"
                  : getLocationName(location),
              value:
                location === "Cancelled"
                  ? "Cancelled"
                  : getLocationName(location),
            }))}
            selected={location || []}
            onChange={handleLocationChange}
            allowMultiple
          />
        ),
        shortcut: true,
      });
    }

    // Add date filter only for the upcoming tab (tab === 8)
    if (title?.includes("Upcoming Orders")) {
      filters.splice(1, 0, {
        key: "date",
        label: "Date",
        filter: (
          <ChoiceList
            title="Date"
            titleHidden
            choices={getUniqueDatesFromOrders(orders).map((date) => ({
              label: date,
              value: date,
            }))}
            selected={selectedFilterDate ? [selectedFilterDate] : []}
            onChange={(selected) => {
              setSelectedFilterDate(selected[0]);
            }}
          />
        ),
        shortcut: true,
      });
    }

    if (ordersOrganizerEnabled) {
      filters.push(
        {
          key: "department",
          label: "Department",
          filter: (
            <ChoiceList
              title="Department"
              titleHidden
              choices={uniqueDepartmentChoices.map((d) => ({
                label: d,
                value: d,
              }))}
              selected={selectedDepartments || []}
              onChange={(sel) =>
                setSelectedDepartments(sel.length ? sel : null)
              }
              allowMultiple
            />
          ),
          shortcut: true,
        },
        {
          key: "pickupTime",
          label: "Pickup time",
          filter: (
            <ChoiceList
              title="Pickup time"
              titleHidden
              choices={uniquePickupChoices.map((d) => ({
                label: d,
                value: d,
              }))}
              selected={selectedPickupTimes || []}
              onChange={(sel) =>
                setSelectedPickupTimes(sel.length ? sel : null)
              }
              allowMultiple
            />
          ),
          shortcut: true,
        },
      );
    }

    const customerName = (order) => {
      if (order.service?.type === "delivery" && order.shipping_address) {
        return order.shipping_address.name;
      } else if (order.billing_address) {
        return order.billing_address.name;
      } else if (order.customer) {
        return `${order.customer.first_name || ""} ${order.customer.last_name}`;
      } else if (order.shipping_address) {
        return order.shipping_address.name;
      }
      return "";
    };

    const hasTag = (order, tag) => {
      const tags = (order?.tags || "")
        .split(",")
        .map((t) => t.trim().toLowerCase());
      return tags.includes(tag.toLowerCase());
    };

    const renderStatusIndicators = (order) => {
      const picked = hasTag(order, "picked");
      const pickedUp = hasTag(order, "picked-up");
      const delivered = hasTag(order, "delivered-to-customer");

      const indicators = [];

      if (picked) {
        indicators.push(
          <FiberManualRecordIcon
            key="picked"
            style={{ color: "green" }}
            titleAccess="Picked"
          />,
        );
      }

      if (pickedUp) {
        indicators.push(
          <CheckCircleIcon
            key="picked-up"
            style={{ color: "#2c6ecb" }}
            titleAccess="Picked up by customer"
          />,
        );
      }

      if (delivered) {
        indicators.push(
          <LocalShippingIcon
            key="delivered"
            style={{ color: "green" }}
            titleAccess="Delivered to customer"
          />,
        );
      }

      if (!indicators.length) {
        indicators.push(
          <FiberManualRecordOutlinedIcon key="pending" titleAccess="Pending" />,
        );
      }

      return (
        <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
          {indicators}
        </div>
      );
    };

    const headers = [
      {
        type: "text",
        label: "Status",
        fn: (order) => {
          if (!user.isAdmin && !user.betaAccess) {
            return "";
          }
          return renderStatusIndicators(order);
        },
      },
      {
        type: "text",
        label: "Order #",
        fn: (order) => (
          <Button
            size="slim"
            plain
            onClick={() => handleSelect(order)}
            color="transparent"
          >
            {order.name}
          </Button>
        ),
      },
      {
        type: "text",
        label: "Ready By",
        fn: (x) => <OrderCollectionDate order={x} />,
      },
      { type: "text", label: "Customer", fn: customerName },
      {
        type: "text",
        label: sanagans ? "Pickup Location" : "",
        fn: (order) => {
          if (!sanagans || !order) return null;
          if (!locationsLoaded) return "Loading...";

          if (order.cancelled_at) return "Cancelled";

          const locationId = order.fulfillments?.[0]?.location_id;
          const assignedLocation = order.fulfillment_order?.assigned_location;

          if (order.fulfillment_status === "fulfilled" && locationId) {
            return (
              getLocationName(locationsMap[locationId]) || "Unknown Location"
            );
          }

          if (assignedLocation) {
            // Prefer the location name from the fulfillment_order if available
            if (assignedLocation.location?.name) {
              return assignedLocation.location.name;
            }
            // Fallback to address lookup for older cached orders
            return (
              getLocationName(assignedLocation.address1) || "Unknown Location"
            );
          }

          return "Cancelled";
        },
      },
      {
        type: "numeric",
        label: "Type",
        fn: (x) =>
          x.cancelled_at ? (
            "Cancelled"
          ) : (
            <React.Fragment>
              <OrderServiceType
                type={orderShippingMethod(x)}
                order={x}
                delivery={deliveries.entities[x.id]}
                navigateExternal={admin.navigateExternal}
              />
              {isBackupRateDeliveryError(x, deliveries) ? (
                <div style={{ textAlign: "right", marginTop: "0.25rem" }}>
                  <Badge status="critical">No Courier Booked</Badge>
                </div>
              ) : null}
            </React.Fragment>
          ),
        sortable: true,
      },
      {
        type: "numeric",
        col: null,
        label: "",
        fn: (order) => (
          <Popover
            active={popover === order.id}
            activator={
              <IconButton onClick={() => setPopover(order.id)}>
                <MoreIcon style={{ cursor: "pointer" }} />
              </IconButton>
            }
            onClose={() => setPopover(null)}
          >
            <ActionList
              items={[
                {
                  content: <React.Fragment>View on Shopify</React.Fragment>,
                  onAction: () => handleView(order),
                },
                ...orderActions(order, deliveries.entities[order.id]),
              ]}
            />
          </Popover>
        ),
      },
    ];

    if (!orders.length) {
      return (
        <Card.Section sectioned>Your orders will show up here.</Card.Section>
      );
    }

    const scheduleDelivery = (order) => {
      setScheduleDeliveryFor(order);
    };

    const handleScheduleDelivery = (order, window, provider) => {
      setSchedulingDelivery(true);
      admin.scheduleDelivery(order.id, window.code, provider).finally(() => {
        setSchedulingDelivery(false);
        setScheduleDeliveryFor(false);
      });
    };

    const changeOrderDate = (order) => {
      setChangeDateFor(order);
    };
    const handleChangeOrderDate = (order, window) => {
      setChangingOrderDate(true);
      admin.changeOrderDate(order.id, window.code).finally(() => {
        setChangingOrderDate(false);
        setChangeDateFor(false);
      });
    };

    const handleCloseModal = () => {
      setScheduleDeliveryFor(false);
      setChangeDateFor(false);
      setChangeLocationFor(false);
    };

    const handleChangeFulfillmentLocation = (order) => {
      setChangeLocationFor(order);
    };

    const handleSaveChangeFulfillmentLocation = (locationId) => {
      setChangingLocation(true);
      admin
        .changeFulfillmentLocation(changeLocationFor.id, locationId)
        .then(() => {
          // Success - close the modal
          setChangeLocationFor(false);
        })
        .catch(() => {
          // Error - keep modal open so user can try again
          // Error message already shown via toast in admin context
        })
        .finally(() => {
          setChangingLocation(false);
        });
    };

    const handleCancelDelivery = (order) => {
      admin.setConfirmation({
        content: "Are you sure you want to cancel this delivery?",
        handleOk: () => admin.cancelDelivery(order.id),
      });
    };

    const orderActions = (order, delivery) => {
      const links = [];
      const type = orderShippingMethod(order);
      const isPickupOrder = type === "pickup";
      const isDeliveryOrder = type === "delivery";
      const pickedUp = hasTag(order, "picked-up");
      const deliveredToCustomer = hasTag(order, "delivered-to-customer");
      // previously used to gate schedule option for store deliveries
      const carrierDelivery =
        type === "delivery" &&
        delivery &&
        order.service &&
        order.service.provider !== STORE_PROVIDER &&
        (delivery.status === "pending" || delivery.status === "completed");
      // Allow scheduling when:
      // - order is pickup (to change to delivery), or
      // - there is no delivery yet, or
      // - existing delivery is cancelled or errored (failed initial scheduling)
      // Do NOT require a third-party carrier for store deliveries.
      let allowSchedulingDelivery =
        type === "pickup" ||
        !delivery ||
        (delivery &&
          (delivery.status === "cancelled" || delivery.status === "error"));

      // Add change fulfillment location action if order has fulfillment info
      if (
        !user.isVendor &&
        (order.fulfillment_order ||
          (order.fulfillments && order.fulfillments.length > 0))
      ) {
        links.push({
          content: "Change Fulfillment Location",
          onAction: () => handleChangeFulfillmentLocation(order),
        });
      }

      if (type === "pickup") {
        links.push({
          content: "Change Date",
          onAction: () => changeOrderDate(order),
        });
      }

      if (allowSchedulingDelivery) {
        links.push({
          content:
            type === "pickup" ? "Convert to Delivery" : "Schedule Delivery",
          onAction: () => scheduleDelivery(order),
        });
      }

      const picked = hasTag(order, "picked");

      if (isPickupOrder && picked && !pickedUp) {
        links.push({
          content: "Mark as Picked Up",
          onAction: () => {
            setPopover(null);
            admin.markOrderPickedUp(order.id);
          },
        });
      }

      if (isDeliveryOrder && picked && !deliveredToCustomer) {
        links.push({
          content: "Mark as Delivered",
          onAction: () => {
            setPopover(null);
            admin.markOrderDelivered(order.id);
          },
        });
      }

      if (carrierDelivery && delivery.shipping_label) {
        const defaultURI =
          delivery.shipping_provider === "Sameday"
            ? `https://www.sddtrack.com/Order/Tools/OrderLabel.aspx?IsOnline=1&ReviewOrderOverride=1&OrderTrackingID_Str=${delivery.shipping_order_id}`
            : delivery.shipping_label.replace("http://", "https://");
        links.push({
          content: "Shipping Label",
          onAction: () => admin.navigateExternal(defaultURI),
        });
      }

      if (carrierDelivery && delivery.status_url) {
        links.push({
          content: "Delivery Status",
          onAction: () => admin.navigateExternal(delivery.status_url),
        });
      }

      if (carrierDelivery && order.service?.provider === "Trexity") {
        const trexityUrl = delivery.shipping_order_id
          ? `https://merchant.trexity.com/shipments/${delivery.shipping_order_id}/dashboard`
          : "https://merchant.trexity.com/";
        links.push({
          content: "View on Trexity",
          onAction: () => admin.navigateExternal(trexityUrl),
        });

        if (delivery?.id) {
          links.push({
            content: "View Label",
            onAction: async () => {
              setPopover(null);
              setParcelLabelOpen(true);
              setParcelLabelLoading(true);
              setParcelLabelFilename(`${order.name} - Parcel Label.pdf`);

              if (parcelLabelUrl) URL.revokeObjectURL(parcelLabelUrl);
              setParcelLabelUrl(null);

              const blob = await admin.fetchBlobFromLink(
                `/api/deliveries/${delivery.id}/label_pdf/0`,
                { headers: { Accept: "application/pdf" } },
              );
              if (blob) setParcelLabelUrl(URL.createObjectURL(blob));
              setParcelLabelLoading(false);
            },
          });
        }
      }

      if (carrierDelivery && order.service?.provider === "Nash") {
        const nashUrl = delivery.shipping_order_id
          ? `https://portal.usenash.com/active/${delivery.shipping_order_id}`
          : "https://portal.usenash.com/";
        links.push({
          content: "View on Nash",
          onAction: () => admin.navigateExternal(nashUrl),
        });
        // console.log(delivery);
      }

      // Show cancel whenever a delivery exists and isn't already cancelled.
      // Rationale: some orders may have stale or missing Method notes,
      // but if a delivery record exists, cancelling should be allowed.
      if (delivery && delivery.status !== "cancelled") {
        links.push({
          content: "Cancel Delivery",
          onAction: () => handleCancelDelivery(order),
          destructive: true,
        });
      }

      return links;
    };

    const handleView = (order) => {
      admin.navigateToShopify(`admin/orders/${order.id}`);
      setPopover(null);
    };

    // (moved up to maintain hook order)

    return (
      <React.Fragment>
        <Card.Header>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <span>
              Showing {results.length} Order{results.length !== 1 ? "s" : ""}
            </span>

            {sanagans && date ? (
              <Button
                loading={admin.loadingEmailPickupInstructions}
                size="slim"
                primary
                disabled={disableEmailer}
                onClick={handleEmailPickupInstructions}
              >
                Email Pickup Instructions
              </Button>
            ) : null}
            <Button
              loading={loadingLineItemSummary}
              size="slim"
              primary
              onClick={handlePrintItemSummary}
            >
              Print Item Summary
            </Button>
            <Button size="slim" primary onClick={handlePrintOrders}>
              Print Orders
            </Button>
          </div>
        </Card.Header>
        <Card.Section>
          {ordersOrganizerEnabled ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <div style={{ minWidth: "200px" }}>
                <Select
                  label="Sort by"
                  options={[
                    { label: "Order #", value: "order_number" },
                    { label: "Last name", value: "last_name" },
                    { label: "Department", value: "department" },
                    { label: "Ready by", value: "ready_by" },
                  ]}
                  value={sortKey}
                  onChange={setSortKey}
                />
              </div>
              <Button
                size="slim"
                onClick={() =>
                  setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                }
              >
                {sortDir === "asc" ? "Ascending" : "Descending"}
              </Button>
            </div>
          ) : null}
          <Filters
            queryValue={queryValue}
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={handleFiltersQueryChange}
            onQueryClear={handleQueryValueRemove}
            onClearAll={handleFiltersClearAll}
          />
          <DataTable
            columnContentTypes={headers.map((h) => h.type)}
            headings={headers.map((h) => h.label)}
            rows={rows.map((order) =>
              headers.map((header) =>
                header.fn(header.col ? order[header.col] : order),
              ),
            )}
            footerContent={`Showing page ${page} of ${Math.ceil(
              results.length / pageSize,
            )} pages`}
            verticalAlign="middle"
          />
          <div style={{ height: "100px", textAlign: "center" }}>
            <Pagination
              label="Orders"
              hasPrevious={page > 1}
              previousKeys={[74]}
              previousTooltip="j"
              onPrevious={() => {
                setPage(page - 1);
              }}
              hasNext={page * pageSize < results.length}
              nextKeys={[75]}
              nextTooltip="k"
              onNext={() => {
                setPage(page + 1);
              }}
            />
          </div>
        </Card.Section>

        <ScheduleDeliveryModal
          order={scheduleDeliveryFor}
          serviceWindows={serviceWindows}
          handleClose={() => handleCloseModal()}
          handleSave={handleScheduleDelivery}
          saving={schedulingDelivery}
          carrier={admin.selectedShop.carrier}
        />

        <SelectTimeSlotModal
          order={changeDateFor}
          title="Change Order Date"
          content="This will change the order date. Ensure that you have communicated the new date to your customer to avoid confusion."
          serviceWindows={serviceWindows}
          handleClose={() => handleCloseModal()}
          handleSave={handleChangeOrderDate}
          saving={changingOrderDate}
        />

        {changeLocationFor && admin.selectedShop?.locations && (
          <ChangeFulfillmentLocationModal
            open={!!changeLocationFor}
            order={changeLocationFor}
            locations={admin.selectedShop.locations}
            currentLocationId={
              changeLocationFor.fulfillment_order?.assigned_location?.location
                ?.id || changeLocationFor.fulfillments?.[0]?.location_id
            }
            onSave={handleSaveChangeFulfillmentLocation}
            onClose={handleCloseModal}
            saving={changingLocation}
          />
        )}

        <ParcelLabelModal
          open={parcelLabelOpen}
          onClose={() => {
            setParcelLabelOpen(false);
            setParcelLabelLoading(false);
            if (parcelLabelUrl) URL.revokeObjectURL(parcelLabelUrl);
            setParcelLabelUrl(null);
          }}
          pdfUrl={parcelLabelUrl}
          filename={parcelLabelFilename}
          loading={parcelLabelLoading}
        />

        <PrintLineItemSummary
          line_items={lineItemSummary}
          title={`Item Summary: ${title}`}
          ref={printItemSummaryComponentRef}
        />

        <PrintOrders
          orders={results}
          deliveries={deliveries}
          products={admin.products}
          title={title}
          showPrices={
            shop?.shop_config?.print_order_show_prices === false ? false : true
          }
          ref={printOrderComponentRef}
        />
      </React.Fragment>
    );
  },
);

export default OrderList;
